export enum ECloudinaryTransform {
  cover16x9 = "cover16x9",
  cover4x3 = "cover4x3",
  cover3x2 = "cover3x2",
  cover2x6 = "cover2x6",
  fill16x9 = "fill16x9",
  fill3x2 = "fill3x2",
  fit16x9 = "fit16x9",
  fit3x2 = "fit3x2",
  limit = "limit",
  bgcrop = "bgcrop",
  thumb3x4 = "thumb3x4",
  thumb9x10 = "thumb9x10",
  thumb1x1 = "thumb1x1",
}

export enum EWsrvTransform {
  cover16x9 = "cover16x9",
  cover4x3 = "cover4x3",
  cover3x2 = "cover3x2",
  cover2x6 = "cover2x6",
  fill16x9 = "fill16x9",
  fill3x2 = "fill3x2",
  fill1x1 = "fill1x1",
  fit16x9 = "fit16x9",
  fit4x3 = "fit4x3", 
  fit3x2 = "fit3x2",
  fit3x4 = "fit3x4",
  fit1x1 = "fit1x1",
  limit = "limit",
  bgcrop = "bgcrop",
  thumb3x4 = "thumb3x4",
  thumb9x10 = "thumb9x10",
  thumb1x1 = "thumb1x1",
}

export interface IMediaItem {
  url: string;
  thumbnailURL?: string;
  dimensions?: [width: number, height: number];
  caption?: string;
  description?: string;
  mimeType?: string;
  sources?: { srcSet: string; media: string }[];
}

export interface IOpenHouseItem {
  startDate: string;
  endDate: string;
  headLine: string;
}

export interface IMakeCloudinaryURL {
  preset: ECloudinaryTransform;
  originURL: string;
  width?: number;
  height?: number;
  background?: string;
  quality?: string;
  cloudinaryFetch?: string;
}

export interface IMakeWesrvURL {
  preset: EWsrvTransform;
  originURL: string;
  width?: number;
  height?: number;
  background?: string;
  quality?: string;
}

export const makeCloudinaryURL = (args: IMakeCloudinaryURL) => {
  let transform = ["f_auto"];
  args.quality ? transform.push("q_" + args.quality) : transform.push("q_auto");
  args.background && transform.push("b_" + args.background);
  if (args.preset != ECloudinaryTransform.bgcrop) {
    args.width && transform.push("w_" + args.width);
  }
  // args.height && transform.push("h_" + args.height);
  switch (args.preset) {
    case ECloudinaryTransform.cover16x9:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.cover4x3:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 3) / 4));
      break;
    case ECloudinaryTransform.cover3x2:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.cover2x6:
      transform.push("c_fill");
      args.width && transform.push("h_" + Math.round((args.width * 6) / 2));
      break;
    case ECloudinaryTransform.fill16x9:
      transform.push("c_lpad");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.fill3x2:
      transform.push("c_lpad");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.fit16x9:
      transform.push("c_fit");
      args.width && transform.push("h_" + Math.round((args.width * 9) / 16));
      break;
    case ECloudinaryTransform.fit3x2:
      transform.push("c_fit");
      args.width && transform.push("h_" + Math.round((args.width * 2) / 3));
      break;
    case ECloudinaryTransform.limit:
      transform.push("c_limit");
      break;
    case ECloudinaryTransform.bgcrop:
      transform.push("c_crop");
      transform.push("g_center");
      transform.push("ar_0.5");
      break;
    case ECloudinaryTransform.thumb3x4:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + Math.round((args.width * 4) / 3));
      break;
    case ECloudinaryTransform.thumb9x10:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + Math.round((args.width * 10) / 9));
      break;
    case ECloudinaryTransform.thumb1x1:
      transform.push("c_fill");
      transform.push("g_face");
      args.width && transform.push("h_" + args.width);
      break;
  }
  return (
    (args.cloudinaryFetch ||
      "https://res.cloudinary.com/dnsr06bev/image/fetch/") +
    transform.join(",") +
    "/" +
    encodeURI(args.originURL)
  );
};

export const makeWsrvURL = (args: IMakeWesrvURL) => {
  let transform = ["output=jpg", "bg=white"];
  args.quality ? transform.push("q=" + args.quality) : transform.push("q=70");
  args.width && transform.push("w=" + args.width);
  switch (args.preset) {
    case EWsrvTransform.cover16x9:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.cover4x3:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 3) / 4));
      break;
    case EWsrvTransform.cover3x2:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.cover2x6:
      transform.push("fit=cover");
      args.width && transform.push("h=" + Math.round((args.width * 6) / 2));
      break;
    case EWsrvTransform.fill16x9:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.fill3x2:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.fill1x1:
      transform.push("fit=contain");
      args.width && transform.push("h=" + Math.round((args.width * 1) / 1));
      break;
    case EWsrvTransform.fit16x9:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 9) / 16));
      break;
    case EWsrvTransform.fit4x3:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 3) / 4));
      break;
    case EWsrvTransform.fit3x2:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 2) / 3));
      break;
    case EWsrvTransform.fit3x4:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 4) / 3));
      break;
    case EWsrvTransform.fit1x1:
      transform.push("fit=inside");
      args.width && transform.push("h=" + Math.round((args.width * 1) / 1));
      break;
    case EWsrvTransform.limit:
      transform.push("fit=inside");
      break;
    case EWsrvTransform.bgcrop:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width / 2)));
      break;
    case EWsrvTransform.thumb3x4:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width * 4) / 3));
      break;
    case EWsrvTransform.thumb9x10:
      transform.push("fit=cover");
      transform.push("a=center");
      args.width && transform.push("h=" + Math.round((args.width * 10) / 9));
      break;
    case EWsrvTransform.thumb1x1:
      transform.push("fit=cover");
      transform.push("a=top-left");
      args.width && transform.push("h=" + args.width);
      break;
  }

  return `//wsrv.nl/?url=${encodeURI(args.originURL)}&${transform.join("&")}`
};